<script setup lang="ts">
import type { Tenant } from '~/types/models';
import { isClientFacingSurface } from '~/lib/brandingSurface';
import { applyWorkspaceBrandTheme, resolveClientFacingLogoUrl } from '~/lib/brandTheme';
import {
  PLATFORM_APP_ICON_PATH,
  PLATFORM_APP_NAME,
  PLATFORM_DARK_BACKGROUND,
  PLATFORM_DESCRIPTION,
  PLATFORM_FAVICON_PATH,
  PLATFORM_LIGHT_BACKGROUND,
} from '~/lib/platformBrand';

const themeBootstrapScript = `
(() => {
  const storedPreference = window.localStorage.getItem('vuey-color-mode');
  const legacyMode = window.localStorage.getItem('vuey-theme-mode');
  const preference = ['system', 'dark', 'light'].includes(storedPreference)
    ? storedPreference
    : (['dark', 'light'].includes(legacyMode) ? legacyMode : 'system');
  const mode = preference === 'dark' || preference === 'light'
    ? preference
    : (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  try {
    const appearance = JSON.parse(window.localStorage.getItem('vuey-user-appearance') || 'null');
    document.documentElement.dataset.density = ['compact', 'default', 'comfortable'].includes(appearance?.density)
      ? appearance.density
      : 'default';
    document.documentElement.dataset.appTheme = appearance?.themeId || 'studio';
  } catch {
    document.documentElement.dataset.density = 'default';
    document.documentElement.dataset.appTheme = 'studio';
  }
  document.documentElement.classList.toggle('dark', mode === 'dark');
  document.documentElement.style.colorScheme = mode;
})();
`;

const tenantStore = useTenantStore();
const route = useRoute();
const { getHostname, isCustomDomain } = useDomainResolver();
const publicWorkspaceBranding = useState<Partial<Tenant> | null>('public-workspace-branding', () => null);
const clientFacingSurface = computed(() => isClientFacingSurface(
  route.path,
  typeof route.query.source === 'string' ? route.query.source : null,
));
const publicBrandingTenantId = computed(() => {
  if (typeof route.params.tenantId === 'string' && route.params.tenantId.trim()) {
    return route.params.tenantId.trim();
  }

  if (typeof route.query.tenantId === 'string' && route.query.tenantId.trim()) {
    return route.query.tenantId.trim();
  }

  return '';
});
const activeBranding = computed(() => (
  clientFacingSurface.value
    ? (publicWorkspaceBranding.value || tenantStore.currentTenant || null)
    : null
));
const activeAppName = computed(() => activeBranding.value?.name || PLATFORM_APP_NAME);
const faviconHref = computed(() => (
  activeBranding.value?.paymentSettings?.faviconURL
  || resolveClientFacingLogoUrl(activeBranding.value)
  || PLATFORM_FAVICON_PATH
));
const appleTouchIconHref = computed(() => (
  activeBranding.value?.paymentSettings?.faviconURL
  || resolveClientFacingLogoUrl(activeBranding.value)
  || PLATFORM_APP_ICON_PATH
));
const headLinks = computed(() => {
  const links: Array<{ key: string; rel: string; href: string; type?: string }> = [
    { key: 'favicon-icon', rel: 'icon', href: faviconHref.value, type: 'image/png' },
    { key: 'favicon-shortcut', rel: 'shortcut icon', href: faviconHref.value, type: 'image/png' },
  ];

  if (!clientFacingSurface.value) {
    links.unshift({ key: 'pwa-manifest', rel: 'manifest', href: '/manifest.webmanifest' });
    links.push({ key: 'favicon-apple', rel: 'apple-touch-icon', href: appleTouchIconHref.value });
  }

  return links;
});

async function syncPublicSurfaceBranding() {
  if (!clientFacingSurface.value) {
    publicWorkspaceBranding.value = null;
    return;
  }

  try {
    const hostname = getHostname();
    const response = await $fetch<{ tenant: Partial<Tenant> | null }>('/api/public/tenant-branding', {
      query: {
        tenantId: publicBrandingTenantId.value || undefined,
        hostname: isCustomDomain(hostname) ? hostname : undefined,
      },
    });

    publicWorkspaceBranding.value = response?.tenant || null;
  } catch (error) {
    console.warn('Unable to load client-facing branding context:', error);
    publicWorkspaceBranding.value = null;
  }
}

await syncPublicSurfaceBranding();

watch(
  activeBranding,
  (branding) => {
    if (clientFacingSurface.value) {
      applyWorkspaceBrandTheme((branding || null) as Tenant | null);
    }
  },
  { immediate: true },
);

watch(
  () => [
    route.path,
    typeof route.query.source === 'string' ? route.query.source : '',
    typeof route.query.tenantId === 'string' ? route.query.tenantId : '',
    typeof route.params.tenantId === 'string' ? route.params.tenantId : '',
    getHostname(),
  ],
  () => {
    void syncPublicSurfaceBranding();
  },
);

useHead(() => ({
  title: activeAppName.value,
  titleTemplate: (title) => {
    const normalizedTitle = String(title || '').trim();
    if (!normalizedTitle || normalizedTitle === activeAppName.value) {
      return activeAppName.value;
    }
    if (clientFacingSurface.value && normalizedTitle === PLATFORM_APP_NAME) {
      return activeAppName.value;
    }
    return `${normalizedTitle} · ${activeAppName.value}`;
  },
  script: [
    {
      key: 'theme-bootstrap',
      tagPosition: 'head',
      innerHTML: themeBootstrapScript,
    },
  ],
  __dangerouslyDisableSanitizersByTagID: {
    'theme-bootstrap': ['innerHTML'],
  },
  meta: [
    {
      key: 'application-name',
      name: 'application-name',
      content: clientFacingSurface.value ? activeAppName.value : PLATFORM_APP_NAME,
    },
    {
      key: 'apple-mobile-web-app-title',
      name: 'apple-mobile-web-app-title',
      content: clientFacingSurface.value ? activeAppName.value : PLATFORM_APP_NAME,
    },
    ...(clientFacingSurface.value
      ? []
      : [
          { key: 'apple-mobile-web-app-capable', name: 'apple-mobile-web-app-capable', content: 'yes' },
          { key: 'mobile-web-app-capable', name: 'mobile-web-app-capable', content: 'yes' },
        ]),
    { key: 'description', name: 'description', content: PLATFORM_DESCRIPTION },
    { key: 'theme-color-light', name: 'theme-color', content: PLATFORM_LIGHT_BACKGROUND, media: '(prefers-color-scheme: light)' },
    { key: 'theme-color-dark', name: 'theme-color', content: PLATFORM_DARK_BACKGROUND, media: '(prefers-color-scheme: dark)' },
  ],
  link: headLinks.value,
}));
</script>

<template>
  <div>
    <NuxtLoadingIndicator color="var(--primary)" :height="3" />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>
